import { dateFromIso, monthName } from '@/i18n/calendar'
import { localeFormatter } from '@/i18n/format'

import { LABEL_GREY, POINT_RADIUS, TEXT_VERTICAL_OFFSET } from './chart-constants'
import { type DataPoint } from './chart-utils'

interface HorizontalAxisProps {
    columnWidth: number
    data: Record<string | number, DataPoint>
    height: number
    isLineGraph: boolean
    labelType: 'date' | 'number'
    locale: string
    values: (string | number)[]
}

function formatLabel(xVal: string | number, labelType: 'date' | 'number', locale: string): string {
    if (labelType === 'date') {
        if (typeof xVal === 'number') {
            // Month number → first letter of month abbreviation
            return monthName(xVal, locale, 'short')?.[0] ?? String(xVal)
        }
        // Date string → "MMM" (first 3 chars of month). An unreadable value labels
        // nothing rather than falling back to the raw string: a bare `2026-13-45` on a
        // tick is the unformatted value reaching the page, the same class of defect as
        // the invalid-date marker.
        const date = dateFromIso(String(xVal))
        return date ? localeFormatter(locale).dateTime(date, { month: 'short' }) : ''
    }
    return String(xVal)
}

export function HorizontalAxis({
    columnWidth,
    data,
    height,
    isLineGraph,
    labelType,
    locale,
    values,
}: HorizontalAxisProps) {
    return (
        <svg>
            {values.map((key) => {
                const point = data[key]
                if (!point) {
                    return null
                }

                const label = formatLabel(point.xVal, labelType, locale)

                return (
                    <text
                        key={String(point.xVal)}
                        x={
                            isLineGraph
                                ? point.xCoord - POINT_RADIUS
                                : point.xCoord + columnWidth / 4
                        }
                        y={height + TEXT_VERTICAL_OFFSET}
                        fill={LABEL_GREY}
                        fontSize={12}
                    >
                        {label}
                    </text>
                )
            })}
        </svg>
    )
}
